/* eslint-disable react/button-has-type */
import classNames from 'classnames';
import React from 'react';
import { Link } from 'wouter';

import { Label } from '../../types/site';

import styles from '../../styles/buttons/button.module.css';

export interface ButtonProps {
    type?: string;
    href?: string | null;
    external?: boolean;
    direct?: boolean;
    target?: string;
    label?: Label | null;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right' | 'inline';
    disabled?: boolean;
    loading?: boolean;
    disableOnLoading?: boolean;
    style?: React.CSSProperties | null;
    className?: string | null;
    iconClassName?: string | null;
    labelClassName?: string | null;
    onClick?: React.MouseEventHandler<HTMLElement> | null;
    children?: Label | null;
}

function Button({
    type = 'button',
    href = null,
    external = false,
    direct = false,
    target = '_blank',
    label = null,
    icon = null,
    iconPosition = 'inline',
    disabled = false,
    loading = false,
    disableOnLoading = true,
    style = null,
    className = null,
    iconClassName = null,
    labelClassName = null,
    onClick = null,
    children = null,
}: ButtonProps) {
    const finalLabel = label || children;
    const text = finalLabel;
    const hasChildren = label !== null && children !== null;
    const hasIcon = icon !== null;
    const hasInlineIcon = hasIcon && (iconPosition === 'inline' || text === null);
    const hasIconColumns = hasIcon && !hasInlineIcon;
    const content = (
        <>
            {hasInlineIcon ? (
                <>
                    <span
                        className={classNames([
                            styles.icon,
                            {
                                [iconClassName]: iconClassName !== null,
                            },
                        ])}
                    >
                        {icon}
                    </span>
                    {text !== null ? (
                        <span
                            className={classNames([
                                styles.label,
                                {
                                    [labelClassName]: labelClassName !== null,
                                },
                            ])}
                        >
                            {text}
                        </span>
                    ) : null}
                </>
            ) : null}
            {hasIconColumns ? (
                <>
                    <span className={classNames([styles.left])}>
                        {iconPosition === 'left' ? icon : null}
                    </span>
                    <span className={classNames([styles.center])}>{text}</span>
                    <span className={classNames([styles.right])}>
                        {iconPosition === 'right' ? icon : null}
                    </span>
                    {hasChildren ? children : null}
                </>
            ) : null}
            {!hasIcon ? finalLabel : null}
            {hasChildren ? children : null}
        </>
    );

    const buttonClassNames = classNames([
        styles.container,
        {
            [styles.withIcon]: hasIcon,
            [styles.withIconColumns]: hasIconColumns,
            [styles.withText]: text !== null,
            [styles.isLink]: href !== null,
            [styles.isDisabled]: disabled,
            [styles.isLoading]: loading,
            [className]: className !== null,
        },
    ]);

    if (href !== null) {
        return external || direct ? (
            <a
                href={href}
                className={buttonClassNames}
                onClick={onClick}
                target={external ? target : null}
                style={style}
            >
                {content}
            </a>
        ) : (
            <Link href={href} className={buttonClassNames} onClick={onClick} style={style}>
                {content}
            </Link>
        );
    }
    return (
        <button
            type={type}
            className={buttonClassNames}
            onClick={onClick}
            disabled={disabled || (disableOnLoading && loading)}
            style={style}
        >
            {content}
        </button>
    );
}

export default Button;
